import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import {
  AccordionContent,
  AccordionItem,
  AccordionRoot,
  AccordionTrigger,
  Tabs,
  Tag,
  Text,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kula-ai/components';
import { FileBracketsAsset } from '@kula-ai/assets';
import { AlertTriangle, Check, CheckCircle, Copy03 } from '@kula-ai/icons-core';
import {
  ESIGN_ACTIVITY_COPY,
  ESIGN_DOCUMENT_TOKENS,
} from '@/constants/esignature';
import type { EsignWizardDocument } from '@/types/esignature';
import {
  formatToken,
  invalidTokensOf,
  isDocumentInvalid,
  tokenDisplayName,
  validTokensOf,
} from './esignDocuments';

const COPY_FEEDBACK_MS = 1000;

interface TokenRowProps {
  token: string;
}

/** Name over a copyable chip — the shape the offer-letter token panel uses. */
const TokenRow = ({ token }: TokenRowProps) => {
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = useCallback(() => {
    navigator.clipboard?.writeText(formatToken(token));
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), COPY_FEEDBACK_MS);
  }, [token]);

  // Figma (7:4983) pitches these rows 74px apart: a 14/24 label, a 6px gap, a
  // 32px chip row, and the list's own 12px between rows.
  return (
    <Flex flexDir="column" gap="6px">
      <Text className="!font-semibold !leading-6 !text-secondary-900">
        {tokenDisplayName(token)}
      </Text>
      <Flex align="center" gap="6px" overflow="hidden">
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Box minW={0} overflow="hidden">
                {/* Figma's chip: 24px tall, 2px/8px padding, 6px radius on
                    secondary-50. kula's Tag is taller and rounder. */}
                <Tag
                  label={formatToken(token)}
                  variant="neutral"
                  size="md"
                  className="!h-6 !rounded-md !border-0 !bg-secondary-50 !px-2 !py-0.5"
                />
              </Box>
            </TooltipTrigger>
            <TooltipContent side="top" collisionPadding={12}>
              {formatToken(token)}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        {/* Figma draws the copy affordance as a bordered square button beside
            the chip, not a bare glyph. */}
        <Box
          as="button"
          type="button"
          aria-label={`Copy ${tokenDisplayName(token)} token`}
          display="flex"
          alignItems="center"
          justifyContent="center"
          w="32px"
          h="32px"
          flexShrink={0}
          borderRadius="6px"
          border="1px solid"
          borderColor="border-color"
          bg="white"
          cursor="pointer"
          color={isCopied ? 'pastel-2-dark' : 'secondary-700'}
          _hover={{ bg: 'secondary-25' }}
          onClick={handleCopy}
        >
          {isCopied ? <Check size={16} /> : <Copy03 size={16} />}
        </Box>
      </Flex>
    </Flex>
  );
};

/**
 * The rail's content sits in a fixed 301px column centred in the 340px panel
 * (Figma 7:4983), not a full-bleed block with padding.
 */
const RailBlock = ({ children }: { children: React.ReactNode }) => (
  <Flex flexDir="column" align="center" pt="20px" pb="16px">
    <Flex flexDir="column" w="301px" gap="16px">
      {children}
    </Flex>
  </Flex>
);

const RailCaption = ({ children }: { children: React.ReactNode }) => (
  <Text variant="overhead" className="!text-secondary-600">
    {children}
  </Text>
);

const AvailableTokens = () => (
  <RailBlock>
    <RailCaption>{ESIGN_ACTIVITY_COPY.AVAILABLE_TOKENS_CAPTION}</RailCaption>
    <Flex flexDir="column" gap="12px">
      {ESIGN_DOCUMENT_TOKENS.map(({ token }) => (
        <TokenRow key={token} token={token} />
      ))}
    </Flex>
  </RailBlock>
);

interface IncludedTokensProps {
  documents: EsignWizardDocument[];
}

const IncludedTokens = ({ documents }: IncludedTokensProps) => {
  // Every file starts expanded: the tokens are the reason this rail exists, and
  // a recruiter checking what a document will carry should not have to open
  // each one. Collapsing is theirs to do, so this is controlled rather than a
  // `defaultValue` — that would only seed the first render, leaving a file
  // uploaded later closed while the rest are open.
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const seen = useRef<Set<string>>(new Set());

  useEffect(() => {
    const fresh = documents
      .map((document) => document.key)
      .filter((key) => !seen.current.has(key));
    if (fresh.length === 0) return;

    fresh.forEach((key) => seen.current.add(key));
    // Only the newly-arrived keys are added, so a file the recruiter collapsed
    // stays collapsed when the next one is uploaded.
    setOpenKeys((previous) => [...previous, ...fresh]);
  }, [documents]);

  if (documents.length === 0) {
    return (
      <Flex
        flexDir="column"
        align="center"
        justify="center"
        gap="16px"
        p="48px 24px"
        textAlign="center"
      >
        <FileBracketsAsset />
        <Text color="subtle">{ESIGN_ACTIVITY_COPY.INCLUDED_TOKENS_EMPTY}</Text>
      </Flex>
    );
  }

  return (
    <Box pt="20px" pb="16px">
      <Box px="16px" mb="16px">
        <RailCaption>{ESIGN_ACTIVITY_COPY.INCLUDED_TOKENS_CAPTION}</RailCaption>
      </Box>
      <AccordionRoot
        type="multiple"
        value={openKeys}
        onValueChange={setOpenKeys}
      >
        {documents.map((document) => {
          const invalidTokens = invalidTokensOf(document.validation);
          const validTokens = validTokensOf(document.validation);
          const isInvalid = isDocumentInvalid(document);

          return (
            <AccordionItem key={document.key} value={document.key}>
              {/* The trigger sizes to its content, and kula wraps the children
                  in a `flex-1` span whose default `min-width: auto` refuses to
                  shrink — so a long filename pushed past the 340px rail rather
                  than clipping. Both need pinning to 0. */}
              <AccordionTrigger className="w-full min-w-0 [&>span]:min-w-0">
                <Flex
                  align="center"
                  gap="8px"
                  flex="1"
                  minW={0}
                  overflow="hidden"
                >
                  <Box flexShrink={0} display="flex">
                    {isInvalid ? (
                      <AlertTriangle
                        size={16}
                        color="var(--kula-colors-error-500)"
                      />
                    ) : (
                      <CheckCircle
                        size={16}
                        color="var(--kula-colors-success-500)"
                      />
                    )}
                  </Box>
                  {/* Uploaded names are long and the rail is a fixed 340px, so
                      clip rather than push the chevron out of the panel. Text's
                      own tooltip only appears when it actually overflows. */}
                  <Box minW={0} overflow="hidden">
                    <Text
                      variant="p"
                      color={isInvalid ? 'error' : undefined}
                      truncate
                      tooltip
                    >
                      {document.filename}
                    </Text>
                  </Box>
                </Flex>
              </AccordionTrigger>
              <AccordionContent>
                <Box p="8px 16px 16px">
                  {invalidTokens.length > 0 && (
                    <Box mb="12px">
                      <Box mb="8px">
                        <Text variant="overhead">
                          {ESIGN_ACTIVITY_COPY.INVALID_TOKENS_HEADING}
                        </Text>
                      </Box>
                      <Flex flexWrap="wrap" gap="4px">
                        {invalidTokens.map((token) => (
                          <Flex
                            key={token}
                            align="center"
                            px="8px"
                            py="2px"
                            bg="pastel-1-light"
                            borderRadius="6px"
                          >
                            <Text variant="p" color="error">
                              {formatToken(token)}
                            </Text>
                          </Flex>
                        ))}
                      </Flex>
                    </Box>
                  )}
                  {validTokens.length > 0 && (
                    <Box>
                      <Box mb="8px">
                        <Text variant="overhead">
                          {ESIGN_ACTIVITY_COPY.VALID_TOKENS_HEADING}
                        </Text>
                      </Box>
                      <Flex flexDir="column" gap="12px">
                        {validTokens.map((token) => (
                          <TokenRow key={token} token={token} />
                        ))}
                      </Flex>
                    </Box>
                  )}
                  {invalidTokens.length === 0 && validTokens.length === 0 && (
                    <Text color="subtle">No tokens found</Text>
                  )}
                </Box>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </AccordionRoot>
    </Box>
  );
};

export interface EsignTokensPanelProps {
  documents: EsignWizardDocument[];
}

/**
 * The wizard's right rail: the six anchors a document may carry, and what each
 * uploaded document actually contains.
 *
 * Defaults to "Tokens included" once anything is uploaded — that is the tab a
 * recruiter needs after a failed validation, and landing on the reference list
 * instead would hide the reason Next is blocked.
 */
export const EsignTokensPanel = ({ documents }: EsignTokensPanelProps) => {
  const hasDocuments = documents.length > 0;

  return (
    <Box
      w="340px"
      flexShrink={0}
      borderLeft="1px solid"
      borderColor="border-color"
      overflowY="auto"
      data-test-id="esign-tokens-panel"
    >
      <Tabs.Root
        // Figma 7:4983: the rail's tab list is inset 16px with a 16px gap, sits
        // 10px down from the panel top, and leaves 20px between its rule and
        // the content below. kula's panel carries a 16px top margin that
        // stacked with the content's own padding and doubled that gap.
        listClassName="pl-4 gap-4"
        tabListWrapperClassName="pt-[10px]"
        panelClassName="!mt-0"
        defaultValue={hasDocuments ? 'included' : 'available'}
        items={[
          {
            value: 'available',
            label: ESIGN_ACTIVITY_COPY.TAB_AVAILABLE,
            panel: <AvailableTokens />,
          },
          {
            value: 'included',
            label: ESIGN_ACTIVITY_COPY.TAB_INCLUDED,
            panel: <IncludedTokens documents={documents} />,
          },
        ]}
      />
    </Box>
  );
};
